import type { ReactNode } from "react"
import { TOAST_WITH_ACTION_DURATION } from "@/constants/ui"
import type { ItineraryEvent, ItineraryEventType } from "@/types/itinerary"
import type { Place } from "@/types/place"

import type {
  PreparedAddItineraryEventAction,
  PreparedUpdateItineraryEventAction,
} from "@/lib/itinerary-action-builders"
import { toastManager } from "@/lib/toast"
import { itineraryEventToastLeading } from "@/lib/toast-leading"

export function showItineraryActionToast(
  action: ItineraryActionToastAction,
  options: ShowItineraryActionToastOptions = {}
) {
  const parts = getItineraryActionToastParts(action, options)
  const statusText = getToastPartsText(parts)
  const { context, silent, undoRedoLabel, onUndoRedoAction, tripSheetOpen } =
    options

  const skipAddRemoveWhenTripSheetOpen =
    tripSheetOpen && (action === "add" || action === "remove")

  if (parts && statusText && !silent && !skipAddRemoveWhenTripSheetOpen) {
    const iconType =
      context?.iconType || (action === "add" ? context?.eventType : undefined)
    const leading =
      !tripSheetOpen && context
        ? itineraryEventToastLeading({
            action,
            place: context.place,
            iconType,
            title: context.title,
          })
        : undefined

    toastManager.add({
      id: context?.place?.entity_ref
        ? `itinerary-action-toast-${context.place.entity_ref}`
        : undefined,
      description: getToastPartsDescription(parts),
      type: leading ? undefined : "info",
      timeout: TOAST_WITH_ACTION_DURATION,
      ...(leading ? { data: { leading } } : {}),
      ...(undoRedoLabel && onUndoRedoAction
        ? {
            actionProps: {
              children: undoRedoLabel,
              onClick: onUndoRedoAction,
            },
          }
        : {}),
    })
  }

  return statusText || "change"
}

export function getItineraryActionToastMessage(
  action: ItineraryActionToastAction,
  options: ItineraryActionToastOptions = {}
) {
  return getToastPartsText(getItineraryActionToastParts(action, options))
}

// splits the message around the dynamic title so we can wrap in no-translate
function getItineraryActionToastParts(
  action: ItineraryActionToastAction,
  options: ItineraryActionToastOptions = {}
): ItineraryActionToastParts | undefined {
  const { context, message } = options
  const title =
    context?.title || (context?.eventType === "flight" ? "Flight" : "")

  if (action === "add") {
    return { key: "add", title, suffix: " added to your trip" }
  }
  if (action === "remove") {
    return { key: "remove", title, suffix: " removed from your trip" }
  }
  if (action === "remove-scheduled-events") {
    return { text: "Your itinerary has been cleared" }
  }
  if (action === "remove-ideas") {
    return { text: "Your ideas have been removed" }
  }
  if (
    action === "bulk-undo" ||
    action === "bulk-redo" ||
    action === "trip-change"
  ) {
    return message === undefined ? undefined : { text: message }
  }
  if (action === "move") {
    return { key: "move", title, suffix: " moved" }
  }
  if (action === "expand") {
    return context?.eventType === "hotel"
      ? { key: "expand-checkout", prefix: "Check-out changed for ", title }
      : {
          key: "expand-end",
          prefix: "End time for ",
          title,
          suffix: " changed",
        }
  }
  if (action === "change") {
    return { key: "change", title, suffix: " changed" }
  }
}

function getToastPartsText(parts: ItineraryActionToastParts | undefined) {
  if (!parts) {
    return undefined
  }
  if ("text" in parts) {
    return parts.text
  }
  return `${parts.prefix ?? ""}${parts.title}${parts.suffix ?? ""}`
}

function getToastPartsDescription(parts: ItineraryActionToastParts): ReactNode {
  if ("text" in parts) {
    return parts.text
  }
  // distinct key per action for Weglot compatability
  return (
    <>
      {parts.prefix}
      <span key={`${parts.key}-title`} className="no-translate">
        {parts.title}
      </span>
      {parts.suffix}
    </>
  )
}

export function getAddItineraryActionToastContext(
  event: ItineraryEvent,
  addAction: PreparedAddItineraryEventAction
): ItineraryActionToastContext {
  return {
    title: event.title,
    eventType: event.type,
    place: addAction.next.place,
    iconType: event.place?.type,
  }
}

export function getUpdateItineraryActionToastContext(
  updateAction: PreparedUpdateItineraryEventAction
): ItineraryActionToastContext {
  return {
    title: updateAction.event.title,
    eventType: updateAction.event.type,
    place: updateAction.next.place || updateAction.event.place,
    iconType: updateAction.event.place?.type || updateAction.next.type,
  }
}

export function getItineraryEventToastContext(
  event: ItineraryEvent
): ItineraryActionToastContext {
  return {
    title: event.title,
    eventType: event.type,
    place: event.place,
    iconType: event.place?.type,
  }
}

export type ItineraryActionToastAction =
  | "add"
  | "remove"
  | ItineraryActionToastClearAction
  | "change"
  | "move"
  | "expand"
  | "bulk-undo"
  | "bulk-redo"
  | "trip-change"

export type ItineraryActionToastClearAction =
  | "remove-scheduled-events"
  | "remove-ideas"

export type ItineraryActionToastContext = {
  title?: string
  eventType?: ItineraryEventType
  iconType?: ItineraryEventType
  place?: Place
}

type ItineraryActionToastParts =
  | { text: string }
  | { key: string; prefix?: string; title: string; suffix?: string }

export type ItineraryActionToastOptions = {
  context?: ItineraryActionToastContext
  message?: string
}

type ShowItineraryActionToastOptions = ItineraryActionToastOptions & {
  silent?: boolean
  undoRedoLabel?: string
  onUndoRedoAction?: () => void
  tripSheetOpen?: boolean
}
